import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styles from '../../assets/styles/Homepage/YogaSchool.module.css';
import axios from 'axios';

import ornament from '../../assets/images/linedesign.png';
import { useNavigate } from 'react-router-dom';
import { FaCrown, FaAward } from 'react-icons/fa';
import { GiLotus } from 'react-icons/gi';

const ENV_API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API = `${ENV_API_BASE_URL}/api/section1`;

/* ── animation variants (unchanged) ── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const slideLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.9, ease: 'easeOut' } },
};

const cardVariant = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: i * 0.15, duration: 0.6, ease: [0.4, 0, 0.2, 1] },
  }),
};

const imageVariant = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: i * 0.2, duration: 0.6, ease: 'easeOut' },
  }),
};

const YogaSchool = () => {
  const [sectionData, setSectionData] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  /* ── fetch Section1 data ── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(API);
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        // Use the first record (there's typically one Section1 entry)
        if (list.length > 0) setSectionData(list[0]);
      } catch (err) {
        console.error('YogaSchool fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  /* ── build image array from the `images` array stored in DB ── */
  const images = sectionData?.images?.length
    ? sectionData.images.map(
        (filename) => `${ENV_API_BASE_URL}/uploads/${filename}`
      )
    : [];

  const infoData = [
    { icon: <FaCrown />, text: "India's No.1 Yoga School in Rishikesh" },
    { icon: <FaAward />, text: 'Certified by Yoga Alliance USA' },
    { icon: <GiLotus />, text: 'Trusted by 13,000+ Students Worldwide' },
  ];

  /* ── skeleton loader while fetching ── */
  if (loading) {
    return (
      <section style={{ padding: '80px 0', textAlign: 'center', opacity: 0.5 }}>
        <p>Loading…</p>
      </section>
    );
  }

  /* ── nothing returned from API — render nothing ── */
  if (!sectionData) return null;

  return (
    <motion.section
      className={styles.section}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className="container">

        {/* ── Headings from API ── */}
        <motion.h2 className={styles.heading} variants={fadeUp}>
          {sectionData.mainHeading}
        </motion.h2>

        <motion.h3 className={styles.subTitle} variants={fadeUp}>
          {sectionData.subHeading}
        </motion.h3>

        <motion.div className={styles.ornamentWrapper} variants={fadeUp}>
          <img src={ornament} alt="divider" className={styles.ornamentImage} />
        </motion.div>

        <motion.p className={styles.rating} variants={fadeUp}>
          ⭐ 5 Rated Yoga School
        </motion.p>

        {/* ── Info Cards (static) ── */}
        <div className={styles.infoGrid}>
          {infoData.map((item, index) => (
            <motion.div
              key={index}
              className={styles.infoCard}
              custom={index}
              variants={cardVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
            >
              <div className={styles.cardIconWrapper}>
                <span className={styles.cardIcon}>{item.icon}</span>
              </div>
              <p className={styles.cardText}>{item.text}</p>
            </motion.div>
          ))}
        </div>

        {/* ── Main Grid ── */}
        <div className={styles.mainGrid}>

          {/* LEFT — heading1 + paragraph from API */}
          <motion.div className={styles.content} variants={slideLeft}>
            <h3 className={styles.innerHeading}>
              {sectionData.heading1}
            </h3>

            <p>{sectionData.paragraph}</p>

            <div className={styles.buttons}>
              {[
                { label: '200 Hour Yoga TTC', path: '/course/200-hour-yttc' },
                { label: '300 Hour Yoga TTC', path: '/course/300-hour-yttc' },
                { label: '500 Hour Yoga TTC', path: '/course/500-hour-yttc' },
              ].map((item, i) => (
                <motion.button
                  key={i}
                  whileHover={{ y: -4, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* RIGHT — img1…img4 from API */}
          <div className={styles.images}>
            {images.length > 0 ? (
              images.map((src, index) => (
                <motion.img
                  key={index}
                  src={src}
                  alt={`Yoga Practice ${index + 1}`}
                  custom={index}
                  variants={imageVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                />
              ))
            ) : (
              <p style={{ opacity: 0.4 }}>No images uploaded yet.</p>
            )}
          </div>

        </div>
      </div>
    </motion.section>
  );
};

export default YogaSchool;